import { z } from 'zod';
import { Action } from './actions.js';
import { ClientQuote } from './clients.js';
import { Uuid } from './common.js';
import { ActionConsequence } from './plans.js';
import { R1Detail } from './r1.js';

/**
 * Lot AX « Client d'un R1 relié à la main » (retour de Maxime du 19/10/2026). Le rapprochement automatique par le
 * nom reste interdit (lot D1b) : un membre actif relie un R1 à un client Axonaut (copie locale `clients`), depuis le
 * volet du R1 de l'agenda ou depuis la carte « Préparer le devis » (« Devis déjà fait »). Gestes INTERNES, sans
 * plan, journalisés (`audit_log`) ; rien n'est écrit dans Axonaut ni dans l'agenda.
 *
 * - `PUT /api/r1/:r1Key/client` `{ clientId }` : relier (ou changer) ; le lien vaut pour toutes les copies du R1
 *   (agendas de chaque membre) et gagne sur le rapprochement par l'adresse d'un invité, synchros suivantes comprises.
 * - `DELETE /api/r1/:r1Key/client` : retirer le lien manuel (retour au rapprochement par l'adresse d'un invité).
 * - `GET /api/clients/:id/quotes` : devis du client (copie locale d'Axonaut), du plus récent au plus ancien.
 * - `POST /api/actions/:id/quote-done` `{ clientId, quoteId }` sur une carte « Préparer le devis » : relie le R1 au
 *   client, note le devis choisi (même daté d'avant le R1) et ferme TOUTES les cartes « Préparer le devis » de ce R1.
 *
 * Erreurs : 404 `R1_NOT_FOUND` (R1 absent de l'agenda), 404 `ACTION_NOT_FOUND`, 409 `ACTION_CLOSED` /
 * `NOT_QUOTE_PREPARE` / `NO_R1`, 422 `CLIENT_UNKNOWN` (client absent de la copie d'Axonaut), 422 `QUOTE_UNKNOWN`
 * (devis inconnu ou d'un autre client).
 */

/** `PUT /api/r1/:r1Key/client`. */
export const R1ClientBody = z.object({ clientId: Uuid });
export type R1ClientBody = z.infer<typeof R1ClientBody>;

/** Réponse de `PUT` et `DELETE /api/r1/:r1Key/client` : le volet relu et les cartes changées par les règles. */
export const R1ClientResult = z.object({ detail: R1Detail, consequences: z.array(ActionConsequence) });
export type R1ClientResult = z.infer<typeof R1ClientResult>;

/** `GET /api/clients/:id/quotes` : tous les devis du client, du plus récent au plus ancien. */
export const ClientQuoteList = z.object({ items: z.array(ClientQuote) });
export type ClientQuoteList = z.infer<typeof ClientQuoteList>;

/** `POST /api/actions/:id/quote-done` : « Devis déjà fait » sur « Préparer le devis ». */
export const QuoteDoneBody = z.object({ clientId: Uuid, quoteId: Uuid });
export type QuoteDoneBody = z.infer<typeof QuoteDoneBody>;

/**
 * Réponse : la carte faite, toutes les cartes « Préparer le devis » de ce R1 fermées par le geste (elle comprise)
 * et les cartes changées par le passage des règles qui suit.
 */
export const QuoteDoneResult = z.object({
  action: Action,
  closedActionIds: z.array(Uuid),
  consequences: z.array(ActionConsequence),
});
export type QuoteDoneResult = z.infer<typeof QuoteDoneResult>;

/** Ce que garde la carte fermée par « Devis déjà fait » (`actions.payload.quoteDone`). */
export const QuoteDoneMark = z.object({
  quoteId: Uuid,
  number: z.string().nullable(),
  clientId: Uuid,
  at: z.string(),
  by: Uuid,
});
export type QuoteDoneMark = z.infer<typeof QuoteDoneMark>;

/** « devis D-2026-041 déjà fait » (ligne de « Fait cette semaine »), null pour une autre carte. */
export function quoteDoneNote(payload: Record<string, unknown> | null | undefined): string | null {
  const parsed = QuoteDoneMark.safeParse(payload?.quoteDone);
  if (!parsed.success) return null;
  return parsed.data.number ? `devis ${parsed.data.number} déjà fait` : 'devis déjà fait';
}
